import React, { FunctionComponent, ReactElement } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { useIntl } from 'react-intl'
import { AccessibilityInfo, findNodeHandle, Platform, TextStyle, View, ViewStyle } from 'react-native'
import RNPickerSelect, { PickerStyle } from 'react-native-picker-select'

import { omit } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { getSelectStyles, SelectStyleType } from './styles/selectStyles'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { IS_WEB } from '../../constants'
import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { useFocusState } from '../../hooks/useFocusState'
import { getInputFieldStyles } from '../../styles/commonStyles'
import { getFontStyles, getTypeStyles } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { BaseInput } from '../formElements/BaseInput'
import { ToolTipTriggerConfig } from '../tooltip/Tooltip'

// This function converts number strings back to numbers because the onValueChange on RNPickerSelect
// transforms the value from a number into a string. This causes the picker to switch back to the default
// since the string value does not equal the numeric value.
export function selectFieldCast(val: string | number): string | number {
  if (typeof val === 'string') {
    const num = Number(val)
    if (val === '' || isNaN(num)) {
      return val
    } else {
      return num
    }
  }
  return val
}

export const SelectField = <T extends string | number>({
  accessibilityLabel,
  label,
  largeLabel,
  subLabel,
  largeSubLabel,
  value,
  onChange,
  onFocus,
  onBlur,
  error,
  options,
  placeholder,
  name,
  readOnly,
  key,
  customWebStyle = {},
  customAndroidStyle = {},
  customIOSStyle = {},
  customTextStyle,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipHoverEnabled,
  active,
  decorativeIcon,
  baseInputStyle,
  useStringValue,
  useTriageStyling,
  hideErrorMessage,
  showErrorAsWarning,
  disablePlaceholder,
  disabled,
  icon,
  autoComplete,
  required,
}: SelectFieldProps<T>) => {
  const { formatMessage } = useIntl()
  placeholder =
    placeholder ??
    formatMessage({
      defaultMessage: 'Select',
      description: 'Placeholder text indicating that the form field contains options where the user can select one',
    })

  const { colors, fontFamilyMetadata, isDarkMode, componentStyles } = useTheme()
  const { isFocused, focusProps, focusVisible } = useFocusState({ onFocus, onBlur, active })
  const isActive = isFocused
  const [focusRef] = useAccessibilityFocus({ active: isActive, delay: 200 })
  const selectedStyle = useTriageStyling ? SelectStyleType.TRIAGE : SelectStyleType.DEFAULT
  const styles = getSelectStyles(colors, focusVisible)[selectedStyle]
  const errorStyles = error ? { borderColor: showErrorAsWarning ? colors.borderWarning : colors.inputOutlineError } : {}
  const textInputStyles = getInputFieldStyles({
    colors,
    fontFamilyMetadata,
    componentStyles,
    active: isActive,
    readOnly,
    error: !!error,
  })

  const prunedStyles = omit(
    {
      ...textInputStyles,
      ...errorStyles,
      ...styles.select,
    },
    'lineHeight',
  )

  const handleValueChange = (value: T) => {
    onChange(value)
    /**
     * Since the native android picker does not have a done/close
     * button we need to call onBlur when the value changes
     */
    if (Platform.OS === 'android' && onBlur) {
      onBlur()
    }
  }

  const handleClose = () => {
    AccessibilityInfo.setAccessibilityFocus(findNodeHandle(focusRef.current)!)
    if (focusRef && focusRef.current && onBlur) {
      onBlur()
    }
  }

  const inputIOS = { ...prunedStyles, ...(customIOSStyle as object) } as TextStyle
  const inputAndroid = {
    ...omit(getTypeStyles(colors, fontFamilyMetadata).bodyDefault, 'lineHeight'),
    height: styles.select.height,
    ...(customAndroidStyle as object),
  } as TextStyle
  const inputWeb: PickerStyle['inputWeb'] = {
    ...getFontStyles(colors, fontFamilyMetadata).body.default,
    // @ts-ignore
    appearance: 'none',
    outline: 'none',
    opacity: 1,
    ...textInputStyles,
    ...styles.select,
    ...(customWebStyle as object),
    ...errorStyles,
    // textIndent incompatible with TextStyle
    textIndent: decorativeIcon ? '30px' : '',
  } as unknown as TextStyle
  const iconContainerWeb =
    Platform.OS === 'web'
      ? {
          pointerEvents: 'none',
        }
      : {}
  const iconContainer = {
    top: 0,
    height: styles.select.height,
    justifyContent: 'center',
    ...styles.iconContainer,
    ...iconContainerWeb,
  } as ViewStyle

  // Generate errorMessageId for proper aria-describedby association
  const errorMessageId = name ? `${name}-error-message` : undefined

  const castedValue = !useStringValue && !isNaN(Number(value)) ? selectFieldCast(value) : value

  return (
    <BaseInput
      label={label}
      subLabel={subLabel}
      largeLabel={largeLabel}
      largeSubLabel={largeSubLabel}
      error={error}
      name={name}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipHoverEnabled={toolTipHoverEnabled}
      style={baseInputStyle}
      hideErrorMessage={hideErrorMessage}
      showErrorAsWarning={showErrorAsWarning}
      errorMessageId={errorMessageId}
      customTextStyle={customTextStyle}
      required={required}
    >
      <View testID={tID('SelectField-pickerSelect-container')}>
        {decorativeIcon}
        <RNPickerSelect
          key={key}
          ref={focusRef}
          disabled={disabled || readOnly}
          onValueChange={handleValueChange}
          onOpen={onFocus}
          onClose={handleClose}
          useNativeAndroidPickerStyle={false}
          style={{
            /* stylelint-disable -- external RNPickerSelect lib will handle react native styling */
            inputIOS,
            inputAndroid,
            inputAndroidContainer: { ...(prunedStyles as ViewStyle), justifyContent: 'center' },
            inputWeb,
            iconContainer,
            placeholder: {
              color: colors.inputTextPlaceholder,
            },
          }}
          Icon={() =>
            readOnly
              ? null
              : (icon ?? (
                  <ChevronIcon
                    fillColor={colors.iconDefault}
                    direction={ChevronIconDirection.DOWN}
                    size={styles.icon?.size}
                  />
                ))
          }
          textInputProps={{
            placeholderTextColor: colors.inputTextPlaceholder,
            ...(error && errorMessageId && { 'aria-describedby': errorMessageId }),
            ...(required && { required: true }),
            // Work around for: https://lyrahealth.atlassian.net/browse/LYRA-10438
            // https://github.com/lawnstarter/react-native-picker-select/issues/636#issuecomment-2585795449
            pointerEvents: 'none',
          }}
          placeholder={
            disablePlaceholder
              ? {}
              : readOnly
                ? { label: '', value: '', key: '' }
                : {
                    label: placeholder,
                    value: '',
                    key: '',
                  }
          }
          items={options}
          itemKey={value}
          value={castedValue}
          pickerProps={{
            accessibilityLabel: accessibilityLabel ?? label ?? subLabel,
            onFocus: IS_WEB ? focusProps.onFocus : undefined,
            onBlur: IS_WEB ? focusProps.onBlur : undefined,
            ...(autoComplete && { autoComplete }),
          }}
          touchableWrapperProps={{
            accessibilityLabel: `${accessibilityLabel ?? label ?? subLabel}, ${value}`,
            accessibilityRole: 'button',
          }}
          darkTheme={isDarkMode}
        />
      </View>
    </BaseInput>
  )
}

export const SelectFieldRFF: FunctionComponent<FieldRenderProps<string>> = ({
  input: { value, onChange, onFocus, onBlur, name },
  placeholder,
  disablePlaceholder,
  meta: { touched, error, submitError, active },
  accessibilityLabel,
  label,
  subLabel,
  options,
  readOnly,
  decorativeIcon,
  customWebStyle = {},
  customAndroidStyle = {},
  customIOSStyle = {},
  customTextStyle,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipHoverEnabled,
  baseInputStyle,
  useStringValue,
  useTriageStyling,
  showErrorAsWarning,
  disabled,
  icon,
  autoComplete,
  required,
}) => {
  return (
    <SelectField
      accessibilityLabel={accessibilityLabel}
      label={label}
      subLabel={subLabel}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      disablePlaceholder={disablePlaceholder}
      error={touched && (error || submitError)}
      options={options}
      name={name}
      readOnly={readOnly}
      decorativeIcon={decorativeIcon}
      customWebStyle={customWebStyle}
      customAndroidStyle={customAndroidStyle}
      customIOSStyle={customIOSStyle}
      customTextStyle={customTextStyle}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipHoverEnabled={toolTipHoverEnabled}
      active={active}
      baseInputStyle={baseInputStyle}
      useStringValue={useStringValue}
      useTriageStyling={useTriageStyling}
      showErrorAsWarning={showErrorAsWarning}
      disabled={disabled}
      icon={icon}
      autoComplete={autoComplete}
      required={required}
    />
  )
}

type SelectFieldProps<T = number | string> = {
  accessibilityLabel?: string
  label?: string
  largeLabel?: boolean
  subLabel?: string
  largeSubLabel?: boolean
  value: T
  key?: string
  onChange: (value: T) => void
  onFocus?: () => void
  onBlur?: () => void
  placeholder?: string
  error?: string
  name?: string
  readOnly?: boolean
  options: Array<{
    label: string
    value: T
  }>
  customWebStyle?: PickerStyle['inputWeb']
  customAndroidStyle?: PickerStyle['inputAndroid']
  customIOSStyle?: PickerStyle['inputIOS']
  customTextStyle?: TextStyle
  toolTipContent?: string | ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipHoverEnabled?: boolean
  active?: boolean
  decorativeIcon?: ReactElement
  baseInputStyle?: ViewStyle
  /** Set this as true to avoid numeric string values (e.g. "121") to  be treated as number (e.g. 121) */
  useStringValue?: boolean
  useTriageStyling?: boolean
  hideErrorMessage?: boolean
  showErrorAsWarning?: boolean
  disablePlaceholder?: boolean
  disabled?: boolean
  icon?: ReactElement
  autoComplete?: string
  required?: boolean
}
